import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Card, Button, message, Space, Typography, Input, InputNumber, Switch,
  Progress, Tag, Table, Empty, Tooltip, Form, Popconfirm, Row, Col,
} from 'antd';
import { PlayCircleOutlined, ReloadOutlined, StopOutlined, EyeOutlined } from '@ant-design/icons';
import {
  createAssignTask, getAssignTask, cancelAssignTask, listAssignTasks,
  BotAvatarAssignTask, CreateRequest,
} from '../services/botAvatarAssignApi';

const { Title, Text } = Typography;

const STATUS_COLORS: Record<string, string> = {
  pending: 'default',
  running: 'processing',
  completed: 'success',
  failed: 'error',
  cancelled: 'warning',
};
const STATUS_LABELS: Record<string, string> = {
  pending: '等待中',
  running: '执行中',
  completed: '已完成',
  failed: '失败',
  cancelled: '已取消',
};

const POLL_INTERVAL_MS = 2000;
const HISTORY_PAGE_SIZE = 10;

const BotAvatarAssign: React.FC = () => {
  const [form] = Form.useForm();
  const [creating, setCreating] = useState(false);
  const [activeTask, setActiveTask] = useState<BotAvatarAssignTask | null>(null);
  const [history, setHistory] = useState<BotAvatarAssignTask[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const loadHistory = useCallback(async (page: number) => {
    setHistoryLoading(true);
    try {
      const data = await listAssignTasks(page, HISTORY_PAGE_SIZE);
      setHistory(data.list || []);
      setHistoryTotal(data.total || 0);
      setHistoryPage(data.page || page);
      // 自动绑定最近一个非终态的为 activeTask（页面刷新场景）
      if (!activeTask && data.list && data.list.length > 0) {
        const recent = data.list[0];
        if (recent.status === 'pending' || recent.status === 'running') {
          setActiveTask(recent);
        }
      }
    } catch (e: any) {
      message.error(e.message || '加载任务列表失败');
    } finally {
      setHistoryLoading(false);
    }
  }, [activeTask]);

  useEffect(() => {
    loadHistory(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPolling = useCallback((taskId: string) => {
    stopPolling();
    pollingRef.current = setInterval(async () => {
      try {
        const t = await getAssignTask(taskId);
        setActiveTask(t);
        if (t.status === 'completed' || t.status === 'failed' || t.status === 'cancelled') {
          stopPolling();
          loadHistory(historyPage);
          if (t.status === 'completed') {
            message.success(`任务完成: 更新 ${t.updatedCount} / ${t.totalCount}`);
          } else if (t.status === 'failed') {
            message.error(`任务失败: ${t.errorMessage || '未知错误'}`);
          } else {
            message.warning('任务已取消');
          }
        }
      } catch {
        // 忽略单次轮询错误
      }
    }, POLL_INTERVAL_MS);
  }, [stopPolling, loadHistory, historyPage]);

  const onCreate = useCallback(async () => {
    try {
      const v = await form.validateFields();
      const req: CreateRequest = {
        pageSize: v.pageSize,
        botFilterIds: v.botFilterIds && v.botFilterIds.trim() ? v.botFilterIds.trim() : undefined,
        syncToPlatform: v.syncToPlatform !== false, // 默认 true
      };
      setCreating(true);
      const { taskId } = await createAssignTask(req);
      message.success('任务已创建,开始执行');
      // 立即拉一次状态再开始 polling
      const t = await getAssignTask(taskId);
      setActiveTask(t);
      startPolling(taskId);
      loadHistory(1);
    } catch (e: any) {
      if (e?.errorFields) return; // form validation error
      message.error(e.message || '创建任务失败');
    } finally {
      setCreating(false);
    }
  }, [form, startPolling, loadHistory]);

  const onCancel = useCallback(async () => {
    if (!activeTask) return;
    try {
      await cancelAssignTask(activeTask.taskId);
      message.success('已请求取消(执行器在下一页边界停下)');
    } catch (e: any) {
      message.error(e.message || '取消失败');
    }
  }, [activeTask]);

  const onView = useCallback(async (taskId: string) => {
    try {
      const t = await getAssignTask(taskId);
      setActiveTask(t);
      if (t.status === 'pending' || t.status === 'running') {
        startPolling(taskId);
      } else {
        stopPolling();
      }
    } catch (e: any) {
      message.error(e.message);
    }
  }, [startPolling, stopPolling]);

  const isRunning = activeTask
    && (activeTask.status === 'pending' || activeTask.status === 'running');
  const percent = activeTask && activeTask.totalCount > 0
    ? Math.min(100, Math.round((activeTask.processedCount / activeTask.totalCount) * 100))
    : 0;

  const columns = [
    { title: 'Task ID', dataIndex: 'taskId', key: 'taskId',
      render: (v: string) => <Text code style={{ fontSize: 11 }}>{v.slice(0, 8)}…</Text> },
    { title: '状态', dataIndex: 'status', key: 'status',
      render: (s: string) => <Tag color={STATUS_COLORS[s]}>{STATUS_LABELS[s] || s}</Tag> },
    { title: '总数', dataIndex: 'totalCount', key: 'totalCount' },
    { title: '更新', dataIndex: 'updatedCount', key: 'updatedCount' },
    { title: '跳过', dataIndex: 'skippedCount', key: 'skippedCount' },
    { title: '失败', dataIndex: 'failedCount', key: 'failedCount' },
    { title: '操作人', dataIndex: 'operator', key: 'operator',
      render: (v: string | null) => v || <Text type="secondary">—</Text> },
    { title: '创建时间', dataIndex: 'createTime', key: 'createTime' },
    { title: '操作', key: 'action', render: (_: any, r: BotAvatarAssignTask) => (
        <Button size="small" type="link" icon={<EyeOutlined />} onClick={() => onView(r.taskId)}>查看</Button>
    )},
  ];

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <Title level={4}>Bot 头像/昵称批量分配</Title>

      <Card size="small" style={{ marginBottom: 16 }} title="新建任务">
        <Form form={form} layout="vertical"
          initialValues={{ pageSize: 200, syncToPlatform: true }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="分页大小" name="pageSize"
                rules={[{ type: 'number', min: 10, max: 1000, message: '10–1000' }]}>
                <InputNumber min={10} max={1000} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="同步到 xmarsLive 平台" name="syncToPlatform" valuePropName="checked"
                tooltip="DB 更新成功后,用 bot 的 token 调 /app/user/updateUserProfile 把 nickname+avatar 推到平台。无 token 的 bot 跳过(计入 platform_failed)。">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <div style={{ marginBottom: 16, padding: 8, background: '#f6f8fa', borderRadius: 4, fontSize: 12, color: '#666' }}>
            <b>新规则:</b> 头像每个 bot 都换;昵称仅当 <code>user_name</code> 以 <code>wallet_</code> 或 <code>google_</code> 开头才换。<br />
            昵称从 <code>nickname_pool.used=0</code> 按 id 升序取,头像从 <code>avatar_library.used_count=0 AND enabled=1</code> 按 id 升序取。<br />
            分配后池子里对应记录会被标记已用(单次使用,不循环)。
          </div>
          <Form.Item label="Bot ID 过滤 (可选)" name="botFilterIds"
            tooltip="逗号分隔; 留空 = 全量">
            <Input placeholder="例: 522,523,524" />
          </Form.Item>
          <Button type="primary" icon={<PlayCircleOutlined />} loading={creating}
            disabled={!!isRunning} onClick={onCreate}>
            {isRunning ? '已有任务在跑' : '开始执行'}
          </Button>
        </Form>
      </Card>

      {activeTask && (
        <Card size="small" style={{ marginBottom: 16 }}
          title={
            <Space>
              <span>当前任务</span>
              <Tag color={STATUS_COLORS[activeTask.status]}>{STATUS_LABELS[activeTask.status] || activeTask.status}</Tag>
              <Text code style={{ fontSize: 11 }}>{activeTask.taskId}</Text>
            </Space>
          }
          extra={
            isRunning && (
              <Popconfirm title="确认取消任务?" onConfirm={onCancel}>
                <Button danger size="small" icon={<StopOutlined />}>取消</Button>
              </Popconfirm>
            )
          }
        >
          <Progress
            percent={percent}
            status={activeTask.status === 'failed' ? 'exception'
              : activeTask.status === 'completed' ? 'success' : 'active'}
            format={() => `${activeTask.processedCount} / ${activeTask.totalCount}`}
          />
          <Row gutter={16} style={{ marginTop: 12 }}>
            <Col span={4}><Text type="secondary">总数</Text><div style={{ fontSize: 18 }}>{activeTask.totalCount}</div></Col>
            <Col span={4}><Text type="secondary">已处理</Text><div style={{ fontSize: 18 }}>{activeTask.processedCount}</div></Col>
            <Col span={4}><Text type="secondary">DB 更新</Text><div style={{ fontSize: 18, color: '#52c41a' }}>{activeTask.updatedCount}</div></Col>
            <Col span={4}>
              <Text type="secondary">实际换昵称</Text>
              <div style={{ fontSize: 18, color: '#1677ff' }}>{activeTask.nicknameSetCount}</div>
            </Col>
            <Col span={4}><Text type="secondary">失败</Text><div style={{ fontSize: 18, color: '#ff4d4f' }}>{activeTask.failedCount}</div></Col>
          </Row>
          {activeTask.syncToPlatform === 1 && (
            <Row gutter={16} style={{ marginTop: 8 }}>
              <Col span={6}>
                <Text type="secondary">平台同步成功</Text>
                <div style={{ fontSize: 18, color: '#52c41a' }}>{activeTask.platformSyncedCount}</div>
              </Col>
              <Col span={6}>
                <Text type="secondary">平台同步失败 / 无 token</Text>
                <div style={{ fontSize: 18, color: '#ff4d4f' }}>{activeTask.platformFailedCount}</div>
              </Col>
            </Row>
          )}
          <Row gutter={16} style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
            <Col span={8}>开始: {activeTask.startTime || '—'}</Col>
            <Col span={8}>结束: {activeTask.endTime || '—'}</Col>
            <Col span={8}>操作人: {activeTask.operator || '—'}</Col>
          </Row>
          {activeTask.errorMessage && (
            <Tooltip title={activeTask.errorMessage}>
              <Text type="danger" ellipsis style={{ display: 'block', marginTop: 8 }}>
                错误: {activeTask.errorMessage}
              </Text>
            </Tooltip>
          )}
        </Card>
      )}

      <Card size="small"
        title="任务历史"
        extra={
          <Button size="small" icon={<ReloadOutlined />} onClick={() => loadHistory(historyPage)}>
            刷新
          </Button>
        }
      >
        {history.length === 0 && !historyLoading ? (
          <Empty description="暂无任务" />
        ) : (
          <Table
            size="small"
            rowKey="taskId"
            loading={historyLoading}
            dataSource={history}
            columns={columns}
            pagination={{
              current: historyPage,
              pageSize: HISTORY_PAGE_SIZE,
              total: historyTotal,
              showSizeChanger: false,
              onChange: (p) => loadHistory(p),
            }}
          />
        )}
      </Card>
    </div>
  );
};

export default BotAvatarAssign;
